import { useMemo, useCallback } from "react";
import { useGetProjectsInfiniteQuery } from "@hooks/index";
import { FastProject } from "@typedefs/interfaces";
import { Option } from "@typedefs/modulesInterfaces";
import type { GetProjectsInfinitePayload } from "@app/features/project/projectTypes";

const ITEMS_PER_PAGE = 10;

interface UsePaginatedProjectsReturn {
  projectOptions: Option[];
  projects: FastProject[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  handleLoadMore: () => void;
  handleLoadPrevious: () => void;
  reset: () => void;
}

/**
 * Custom hook for handling paginated project fetching with load more functionality
 * using RTK Query's infinite query
 * @param options Configuration options for the projects query
 * @returns Object containing projects data, loading states, and pagination controls
 */
export const usePaginatedProjects = (
  options: GetProjectsInfinitePayload = {}
): UsePaginatedProjectsReturn => {
  const { page_size = ITEMS_PER_PAGE, ...queryOptions } = options;

  const {
    data: projectsResponse,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
  } = useGetProjectsInfiniteQuery(
    {
      page_size,
      ...queryOptions
    }
  );

  const { projects, projectOptions } = useMemo(() => {
    const projects = projectsResponse?.pages.flatMap(page => page.results) ?? [];
    const projectOptions = projects.map((project) => ({
      name: project.name,
      id: project.id
    } as Option));
    return { projects, projectOptions };
  }, [projectsResponse]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleLoadPrevious = useCallback(() => {
    if (hasPreviousPage && !isFetchingPreviousPage) {
      fetchPreviousPage();
    }
  }, [hasPreviousPage, isFetchingPreviousPage, fetchPreviousPage]);

  const reset = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    projectOptions,
    projects,
    isLoading: isLoading || isFetching,
    isLoadingMore: isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    hasPreviousPage: Boolean(hasPreviousPage),
    handleLoadMore,
    handleLoadPrevious,
    reset,
  };
}; 